import {
  ApiComponentType,
  ApiProjectSettingKey,
  ApiSimpleComponent,
} from "@incendium/api";
import { deleteComponent, saveComponent } from "features/campaigns";
import { usePollPendingComponents } from "features/campaigns/hooks/usePollPendingComponents";
import { useLanderLandingComponents } from "features/lightningLanders/hooks/useLanderLandingComponents";
import NewComponentDialog from "features/lightningLanders/components/NewComponentDialog";
import {
  landerDefaultTheme,
} from "features/lightningLanders/providers/LightningLanderThemeProvider";
import { BlocksList } from "features/sharedComponentBlocks/components/BlocksList";
import { useSelectedProject } from "Hooks";
import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import produce from "immer";
import { friendlyDateTime } from "Helpers/dates";

function LightningComponentsPage() {
  const { selectedProject, projectSettings } = useSelectedProject();
  const { components, setComponents, fetched } = useLanderLandingComponents();
  const history = useHistory();
  const [dialogOpen, setDialogOpen] = useState(false);

  usePollPendingComponents({
    projectId: selectedProject?.id,
    components,
    setComponents,
    interval: 4000,
    enabled: !!selectedProject?.id && components.length > 0,
  });

  const newComponent = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const editComponent = useCallback(
    (component: ApiSimpleComponent) => {
      history.push(`./lightning-landing-components/${component.id}`);
    },
    [history]
  );

  const onDelete = useCallback(
    async (component: ApiSimpleComponent): Promise<string> => {
      await deleteComponent(selectedProject?.id, component.id);
      return `Component ${component.name} deleted`;
    },
    [selectedProject]
  );

  const handleConfirmNewComponent = useCallback(
    async (name: string, aiComponent?: ApiSimpleComponent) => {
      if (!selectedProject?.id) return;

      let component: ApiSimpleComponent;
      if (aiComponent?.id) {
        component = aiComponent;
      } else {
        const theme =
          projectSettings?.[ApiProjectSettingKey.LANDING_THEME] ||
          JSON.stringify(landerDefaultTheme);
        const res = await saveComponent(selectedProject.id, {
          name: name || `Component (${friendlyDateTime(new Date())})`,
          type: ApiComponentType.LANDING_COMPONENT,
          theme,
        });
        component = res.component!;
      }

      setComponents((prev) =>
        produce(prev, (draft) => {
          draft.push(component);
        })
      );
      history.push(`./lightning-landing-components/${component.id}`);
    },
    [selectedProject, projectSettings, setComponents, history]
  );

  return (
    <>
      <NewComponentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirmNewComponent}
      />
      <BlocksList
      blocks={components}
      setBlocks={setComponents}
      fetched={fetched}
      title="Landing Components"
      newButtonLabel="New Landing Component"
      emptyMessage="You don't have any landing components yet."
      emptySubMessage="Create product cards, buttons, headers, and other reusable components to use in blocks and pages."
      onNew={newComponent}
      onEdit={editComponent}
      onDelete={onDelete}
    />
    </>
  );
}

export default LightningComponentsPage;
